import { jsx, jsxs } from "react/jsx-runtime";
import { node, bool, arrayOf, oneOfType, checkPropTypes, string, func, object } from "prop-types";
import React, { useEffect } from "react";
import FocusLock from "@ingka/focus-lock";
import cx from "classnames";
import { escFunction, handleAnimationEnd, onBackdropClick, onVisibleChanged, enter, releaseBody } from "@ingka/modal-shared";
const modalTypes = ["Sheets", "Prompt", "Theatre"];
const propTypes = {
  /** Toggles the rendering of the modal */
  visible: bool,
  children: (props, propName, componentName) => {
    const childArr = React.Children.toArray(props.children).filter((ch) => modalTypes.indexOf(ch?.type?.displayName) > -1);
    if (childArr.length !== 1) {
      console.warn(`@ingka/modal: Invalid children count or type supplied to Modal component. Modal component requires exactly 1 child of the type Sheets or Theatre or Prompt. You've provided: ${childArr.length}.
Leaving the children propType value as it is may cause the component to not function as intended.`);
    }
    return checkPropTypes({
      children: oneOfType([
        arrayOf(node),
        node
      ]).isRequired
    }, props, "prop", componentName);
  },
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Additional class name to be added to the root element */
  className: string,
  /** Callback function when close button is clicked and default callback for footer buttons [Required] */
  handleCloseBtn: func.isRequired,
  /** Tuning focus lock feautre https://npm.m2.blue.cdtapps.com/-/web/detail/@ingka/focus-lock */
  focusLockProps: object,
  /** Keep the modal's content in the DOM when modal is closed */
  keepContentMounted: bool,
  /** Allows closing the modal by clicking outside or pressing `ESC` */
  escapable: bool,
  /** Called after the modal's closing animation is complete, use it to safely remove the Modal from the tree after closing */
  onModalClosed: func,
  /** Called after the modal's opening animation is complete */
  onModalOpened: func,
  /** Controls if the Modal should block the document's body tag scrolling (only disable it for experimental use cases) */
  scrollLock: bool
};
const elx = (...listeners) => (...args) => listeners.map((l) => l && l(...args));
const Modal = (props) => {
  const {
    className,
    visible,
    prefix = "",
    children,
    handleCloseBtn,
    focusLockProps = {},
    keepContentMounted = false,
    escapable = true,
    onModalClosed,
    onModalOpened,
    scrollLock = true,
    ...other
  } = props;
  const [isOpen, setIsOpen] = React.useState(visible);
  const [animationMountHelper, setAnimationMountHelper] = React.useState(false);
  const animationFrameRef = React.useRef(-1);
  const baseClass = `${prefix}modal-wrapper`;
  useEffect(
    () => {
      onVisibleChanged(prefix, visible, scrollLock, isOpen, setIsOpen, setAnimationMountHelper);
    },
    [visible]
  );
  useEffect(() => {
    if (visible)
      enter(setIsOpen, scrollLock, prefix);
    return () => {
      if (scrollLock)
        releaseBody(prefix);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);
  const overlayStyle = cx(
    baseClass,
    isOpen ? `${baseClass}--open` : `${baseClass}--close`,
    className
  );
  const backdropStyle = cx(
    `${baseClass}__backdrop`,
    `${baseClass}__backdrop--dark`
  );
  const hookedModal = React.Children.map(children, (child) => React.cloneElement(child, {
    open: isOpen,
    closeBtnClick: handleCloseBtn,
    prefix
  }));
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ...other,
      onKeyDown: (e) => {
        elx(other.onKeyDown, escFunction(e, baseClass, escapable, handleCloseBtn, setIsOpen, setAnimationMountHelper));
      },
      className: overlayStyle,
      tabIndex: "-1",
      children: [
        /* @__PURE__ */ jsx(
          "div",
          {
            className: backdropStyle,
            onMouseDown: (e) => {
              onBackdropClick(e, escapable, handleCloseBtn);
            },
            onAnimationEnd: (e) => {
              handleAnimationEnd(e, animationFrameRef.current, isOpen, onModalOpened, onModalClosed, scrollLock, animationMountHelper, setAnimationMountHelper, prefix);
            }
          }
        ),
        (keepContentMounted || isOpen || animationMountHelper) && /* @__PURE__ */ jsx(FocusLock, { prefix, autoFocus: true, onEscaped: handleCloseBtn, locked: isOpen, ...focusLockProps, children: hookedModal })
      ]
    }
  );
};
Modal.propTypes = propTypes;
var Modal_default = Modal;
export {
  Modal_default as default
};
